import { apiClient } from "../../../infra/api-client"
import { BASE_URL } from "../../../global"
import type { BmdStrand } from "../../bmd/types/bmd.types"
import type {
  EngDiagListResponse,
  EngDiagQuestion,
  EngDiagQuestionResponse,
  EngDiagStudentAnswers,
  EngDiagSubmitResponse,
} from "../types/eng-diag.types"

const ENDPOINTS = {
  listQuestions: "engDiag/listQuestions",
  getQuestion: "engDiag/getQuestion",
  getYearLevels: "engDiag/getYearLevels",
  previewQuestions: "engDiag/previewQuestions",
  getStrandsForYear: "engDiag/getStrandsForYear",
  getAssignedPreview: "engDiag/getAssignedPreview",
  getAnswersAwaitingMarking: "engDiag/getAnswersAwaitingMarking",
  markAnswer: "engDiag/markAnswer",
  getExistingForSession: "engDiag/getExistingForSession",
  createAssignment: "engDiag/createAssignment",
  submitAnswer: "engDiag/submitAnswer",
  getPrereqCandidates: "engDiag/getPrereqCandidates",
  previewPrereqAssignment: "engDiag/previewPrereqAssignment",
  createPrereqAssignment: "engDiag/createPrereqAssignment",
  getAudio: "engDiag/getAudio",
} as const

export type EngDiagYearLevel = {
  year_level_id: number
  level_name: string
  level_order: number
}

export type EngDiagYearLevelsResponse = {
  source: string
  year_levels: EngDiagYearLevel[]
}

/**
 * The clusters at a year, in the shape the math cluster picker takes: an
 * English cluster is a strand, an English topic is a topic. Sharing the type
 * is what lets the assign panel reuse BmdStrandPicker as-is.
 */
export type EngDiagStrandsResponse = {
  source: string
  year_level_id: number | null
  strands: BmdStrand[]
  /** Topics the student already has at this year, in any tutoring session. */
  locked_topic_ids: number[]
}

/** An English diagnostics already in this tutoring session, from engDiag/getExistingForSession. */
export type EngDiagAssignment = {
  pk_session_key: string
  pk_lesson_activity_key: string
  fk_lesson_key: string
  year_level_id: number
  level_name: string
  flow_status: "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED"
  is_homework: boolean
  topic_count: number
  question_count: number
  started_on: string | null
  completed_on: string | null
  created_on: string
}

export type EngDiagExistingResponse = {
  source: string
  assignments: EngDiagAssignment[]
}

export type EngDiagCreateAssignmentResponse = {
  status: 0 | 1
  error?: string
  pk_session_key?: string
  pk_lesson_activity_key?: string
  fk_lesson_key?: string
  /** Topics and questions added by this call, not the session's totals. */
  topic_count?: number
  question_count?: number
  /** True when the topics went into a test already in this session. */
  merged?: boolean
  message?: string
}

export type EngDiagPreviewResponse = {
  source: string
  year_level_id: number | null
  question_count: number
  questions: EngDiagQuestion[]
  error?: string
}

/** Preview of an already-assigned test, from engDiag/getAssignedPreview. */
export type EngDiagAssignedPreviewResponse = EngDiagPreviewResponse & {
  level_name?: string
  flow_status?: "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED"
}

export async function listEngDiagQuestions(
  signal?: AbortSignal,
): Promise<EngDiagListResponse> {
  const response = await apiClient.post<EngDiagListResponse>(
    BASE_URL + ENDPOINTS.listQuestions,
    {},
    { signal },
  )
  return response.data
}

export async function getEngDiagQuestion(
  questionId: string,
  signal?: AbortSignal,
): Promise<EngDiagQuestionResponse> {
  const response = await apiClient.post<EngDiagQuestionResponse>(
    BASE_URL + ENDPOINTS.getQuestion,
    { question_id: questionId },
    { signal },
  )
  return response.data
}

export async function getEngDiagYearLevels(
  req: { subject_id: number; student_key: string },
  signal?: AbortSignal,
): Promise<EngDiagYearLevelsResponse> {
  const response = await apiClient.post<EngDiagYearLevelsResponse>(
    BASE_URL + ENDPOINTS.getYearLevels,
    req,
    { signal },
  )
  const data = response.data
  return {
    source: data?.source || "fixture",
    year_levels: Array.isArray(data?.year_levels) ? data.year_levels : [],
  }
}

/** Questions at a year; only those in `topic_ids` when given. */
export async function getEngDiagPreviewQuestions(
  req: { year_level_id: number; subject_id?: number; topic_ids?: number[] },
  signal?: AbortSignal,
): Promise<EngDiagPreviewResponse> {
  const response = await apiClient.post<EngDiagPreviewResponse>(
    BASE_URL + ENDPOINTS.previewQuestions,
    req,
    { signal },
  )
  return response.data
}

/** The frozen questions of an assigned test, in serving order. Refused unless the tutor shares an enrolment with the student. */
export async function getEngDiagAssignedPreview(
  req: { pk_activity_key: string },
  signal?: AbortSignal,
): Promise<EngDiagAssignedPreviewResponse> {
  const response = await apiClient.post<EngDiagAssignedPreviewResponse>(
    BASE_URL + ENDPOINTS.getAssignedPreview,
    req,
    { signal },
  )
  return response.data
}

export async function getEngDiagStrandsForYear(
  req: { year_level_id: number; student_key?: string },
  signal?: AbortSignal,
): Promise<EngDiagStrandsResponse> {
  const response = await apiClient.post<EngDiagStrandsResponse>(
    BASE_URL + ENDPOINTS.getStrandsForYear,
    req,
    { signal },
  )
  const data = response.data
  return {
    source: data?.source || "fixture",
    year_level_id: data?.year_level_id ?? null,
    strands: Array.isArray(data?.strands) ? data.strands : [],
    locked_topic_ids: Array.isArray(data?.locked_topic_ids)
      ? data.locked_topic_ids
      : [],
  }
}

export async function getEngDiagExistingForSession(
  req: { session_key: string; student_key: string },
  signal?: AbortSignal,
): Promise<EngDiagExistingResponse> {
  const response = await apiClient.post<EngDiagExistingResponse>(
    BASE_URL + ENDPOINTS.getExistingForSession,
    req,
    { signal },
  )
  return response.data
}

/**
 * Assigns topics at a year. Topics land in the test already at that year in
 * this session if there is one, unless force_new asks for a separate test.
 */
export async function createEngDiagAssignment(req: {
  student_key: string
  session_key: string
  lesson_key?: string
  year_level_id: number
  topic_ids: number[]
  is_homework?: 0 | 1
  force_new?: boolean
}): Promise<EngDiagCreateAssignmentResponse> {
  const response = await apiClient.post<EngDiagCreateAssignmentResponse>(
    BASE_URL + ENDPOINTS.createAssignment,
    req,
  )
  return response.data
}

export async function submitEngDiagAnswer(payload: {
  question_id: string
  student_answers: EngDiagStudentAnswers
  activity_key?: string
}): Promise<EngDiagSubmitResponse> {
  const response = await apiClient.post<EngDiagSubmitResponse>(
    BASE_URL + ENDPOINTS.submitAnswer,
    payload,
  )
  return response.data
}

/** A misconception the tutor can attribute a wrong answer to. */
export type EngDiagMisconceptionType = {
  code: string
  label: string
  description?: string | null
}

/**
 * One answer waiting on the tutor. `question.options` arrives whole here — unlike the student
 * endpoints, a tutor needs the accepted answers in front of them to judge what was written.
 *
 * `target_ref` is "" for the question as a whole, or a sub-question id for reading
 * comprehension, where each sub-question is marked on its own.
 */
export type EngDiagAnswerToMark = {
  pk_attempt_key: string
  session_question_key: string
  target_ref: string
  question: EngDiagQuestion
  response: Record<string, string>
  topic_name: string | null
  question_order: number
  skill_id: number | null
  skill_name: string | null
  answered_on: string
  duration_seconds: number | null
}

export type EngDiagMarkingResponse = {
  source: string
  error?: string
  pk_session_key?: string
  level_name?: string
  flow_status?: "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED"
  misconception_types: EngDiagMisconceptionType[]
  answers: EngDiagAnswerToMark[]
}

export type EngDiagMarkAnswerResponse = {
  status: 0 | 1
  error?: string
  /** How many are still waiting after this one. */
  pending_count?: number
}

/** The answers in an assigned test that are waiting on a tutor. */
export async function getEngDiagAnswersAwaitingMarking(
  req: { pk_activity_key: string },
  signal?: AbortSignal,
): Promise<EngDiagMarkingResponse> {
  const response = await apiClient.post<EngDiagMarkingResponse>(
    BASE_URL + ENDPOINTS.getAnswersAwaitingMarking,
    req,
    { signal },
  )
  const data = response.data
  return {
    ...data,
    misconception_types: Array.isArray(data?.misconception_types) ? data.misconception_types : [],
    answers: Array.isArray(data?.answers) ? data.answers : [],
  }
}

/**
 * Record a verdict on one answer. Only once marked does it reach the report, the skill
 * builders and the prerequisites.
 */
export async function markEngDiagAnswer(req: {
  pk_activity_key: string
  attempt_key: string
  is_correct: boolean
  misconception_type_code?: string
  implicated_skill_ids?: number[]
}): Promise<EngDiagMarkAnswerResponse> {
  const response = await apiClient.post<EngDiagMarkAnswerResponse>(
    BASE_URL + ENDPOINTS.markAnswer,
    req,
  )
  return response.data
}

/**
 * The prerequisite test built from what a student got wrong in a base diagnostic.
 *
 * Its own activity on the lesson plan, not an extension of the test they already sat - the same
 * shape as the maths one. `skipped_no_question` lists skills a chain wants to ask about but has
 * no question written for, which is worth showing rather than hiding.
 */
export interface EngDiagPrereqCluster {
  cluster_id: number
  display_name: string
  question_count: number
  skills: { skill_id: number; skill_name: string | null; level: number }[]
}

export interface EngDiagPrereqPreview {
  status: number
  already_assigned: boolean
  pk_session_key: string | null
  pk_lesson_activity_key: string | null
  /** Every owed question, across all clusters. The panel totals the ticked ones itself. */
  question_count: number
  wrong_base_count: number
  /** Unfiltered: every cluster with something owed, so the tutor can add one left out before. */
  clusters: EngDiagPrereqCluster[]
  /** Clusters this test is already committed to, or null when it is unrestricted. */
  selected_cluster_ids: number[] | null
  skipped_no_question: { skill_id: number; skill_name: string | null }[]
  rules: { max_levels: number; follow_mode: string; multi_skill: boolean }
  error?: string
}

export interface EngDiagPrereqCreateResponse {
  status: number
  created: boolean
  pk_session_key: string | null
  pk_lesson_activity_key: string | null
  question_count: number
  skipped_no_question: { skill_id: number; skill_name: string | null }[]
  message?: string
  error?: string
}

/** A base diagnostic a prerequisite test could be built from. */
export interface EngDiagPrereqCandidate {
  pk_session_key: string
  level_name: string
  flow_status: string
  created_on: string
  completed_on: string | null
  base_question_count: number
  wrong_answer_count: number
  /** A prerequisite test already exists for it; assigning again only adds what is newly owed. */
  already_assigned: boolean
  prereq_session_key: string | null
}

/**
 * The diagnostics this student has sat that could seed a prerequisite test.
 *
 * Only ones with a wrong answer come back - there is nothing to chain from otherwise.
 */
export async function getEngDiagPrereqCandidates(
  req: { student_key: string },
  signal?: AbortSignal,
): Promise<{ status: number; candidates: EngDiagPrereqCandidate[]; error?: string }> {
  const response = await apiClient.post<{
    status: number
    candidates: EngDiagPrereqCandidate[]
    error?: string
  }>(BASE_URL + ENDPOINTS.getPrereqCandidates, req, { signal })
  return response.data
}

/** What a prerequisite test would contain right now. Writes nothing. */
export async function previewEngDiagPrereqAssignment(
  req: { parent_session_key: string; student_key: string },
  signal?: AbortSignal,
): Promise<EngDiagPrereqPreview> {
  const response = await apiClient.post<EngDiagPrereqPreview>(
    BASE_URL + ENDPOINTS.previewPrereqAssignment,
    req,
    { signal },
  )
  return response.data
}

/**
 * Create it, or add whatever the chains now owe.
 *
 * Safe to call again: it writes only questions that do not already exist, so running it after
 * the student has answered more of the base test adds the newly owed ones without duplicates.
 */
export async function createEngDiagPrereqAssignment(req: {
  parent_session_key: string
  student_key: string
  is_homework?: 0 | 1
  /** The sections the tutor ticked. Omitted means every section; empty is rejected server-side. */
  cluster_ids?: number[]
}): Promise<EngDiagPrereqCreateResponse> {
  const response = await apiClient.post<EngDiagPrereqCreateResponse>(
    BASE_URL + ENDPOINTS.createPrereqAssignment,
    req,
  )
  return response.data
}

/**
 * The bytes of one audio clip in a diagnostics question.
 *
 * Clips live in the database, not on a CDN, and the endpoint needs the tutor's token - so a
 * plain <audio src> cannot reach them. The caller turns this blob into an object URL.
 */
export async function fetchEngDiagAudio(
  audioId: string,
  signal?: AbortSignal,
): Promise<Blob> {
  const response = await apiClient.post(
    BASE_URL + ENDPOINTS.getAudio,
    { audio_id: audioId },
    { responseType: "blob", signal },
  )
  return response.data as Blob
}
